import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, User, Building, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import ConfirmModal from '@/components/ConfirmModal';

const Pessoas = () => {
  const [pessoas, setPessoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, id: null });
  const [editingPessoa, setEditingPessoa] = useState(null);
  const [formData, setFormData] = useState({
    tipo: 'PF',
    nome_razao: '',
    fantasia: '',
    cpf_cnpj: '',
    rg_ie: '',
    nasc_abertura: '',
    email: '',
    telefone: '',
    celular: '',
    endereco: { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '' },
    cliente: false,
    fornecedor: false,
    motorista: false,
    funcionario: false,
    chave_pix: '',
    observacoes: '',
    percentual_acerto: ''
  });

  useEffect(() => {
    loadPessoas();
  }, []);

  const loadPessoas = async (search = '') => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await api.get(`/pessoas${params}`);
      setPessoas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pessoas');
    } finally {
      setLoading(false);
    }
  };

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        loadPessoas(searchTerm);
      }
    }, 300); // Aguarda 300ms após parar de digitar

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevenir duplo clique
    
    setSaving(true);
    try {
      // Limpar CPF/CNPJ antes de enviar
      const dataToSend = {
        ...formData,
        cpf_cnpj: formData.cpf_cnpj.replace(/\D/g, ''),
        telefone: formData.telefone.replace(/\D/g, ''),
        celular: formData.celular.replace(/\D/g, ''),
        percentual_acerto: formData.percentual_acerto !== '' ? parseFloat(formData.percentual_acerto) : null,
      };
      
      if (editingPessoa) {
        await api.put(`/pessoas/${editingPessoa.id}`, dataToSend);
        toast.success('Pessoa atualizada com sucesso!');
      } else {
        await api.post('/pessoas', dataToSend);
        toast.success('Pessoa cadastrada com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      loadPessoas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar pessoa');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (pessoa) => {
    setEditingPessoa(pessoa);
    setFormData({ ...pessoa, percentual_acerto: pessoa.percentual_acerto ?? '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/pessoas/${id}`);
      toast.success('Pessoa inativada com sucesso!');
      loadPessoas();
    } catch (error) {
      toast.error('Erro ao inativar pessoa');
    }
  };

  const resetForm = () => {
    setEditingPessoa(null);
    setFormData({
      tipo: 'PF',
      nome_razao: '',
      fantasia: '',
      cpf_cnpj: '',
      rg_ie: '',
      nasc_abertura: '',
      email: '',
      telefone: '',
      celular: '',
      endereco: { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '' },
      cliente: false,
      fornecedor: false,
      motorista: false,
      funcionario: false,
      chave_pix: '',
      observacoes: '',
      percentual_acerto: ''
    });
  };

  const formatCPFCNPJ = (value) => {
    if (!value) return '-';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (numbers.length === 14) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  const formatarCPFCNPJInput = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, (_, p1, p2, p3, p4) => {
        let result = p1;
        if (p2) result += `.${p2}`;
        if (p3) result += `.${p3}`;
        if (p4) result += `-${p4}`;
        return result;
      });
    } else {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, (_, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += `.${p2}`;
        if (p3) result += `.${p3}`;
        if (p4) result += `/${p4}`;
        if (p5) result += `-${p5}`;
        return result;
      });
    }
  };

  const formatarTelefone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, (_, p1, p2, p3) => {
        let result = '';
        if (p1) result = `(${p1}`;
        if (p2) result += `) ${p2}`;
        if (p3) result += `-${p3}`;
        return result;
      });
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, (_, p1, p2, p3) => {
        let result = '';
        if (p1) result = `(${p1}`;
        if (p2) result += `) ${p2}`;
        if (p3) result += `-${p3}`;
        return result;
      });
    }
  };

  // Busca já é feita no backend via API
  const filteredPessoas = pessoas;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="pessoas-page">
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, id: null })}
        onConfirm={() => handleDelete(confirmDelete.id)}
        title="Inativar Pessoa"
        message="Deseja realmente inativar esta pessoa? Esta ação pode ser revertida posteriormente."
        confirmText="Sim, Inativar"
        cancelText="Cancelar"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Cadastros</h1>
          <p className="text-slate-600">Gerencie clientes, fornecedores, motoristas e funcionários</p>
          <p className="text-sm text-slate-500 mt-1">
            <strong>{pessoas.length}</strong> cadastros | <strong>{pessoas.filter(p => p.ativo).length}</strong> ativos
          </p>
        </div>
        <Button 
          data-testid="add-pessoa-button" 
          onClick={() => { resetForm(); setDialogOpen(true); }} 
          className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Cadastro
        </Button>
      </div>

      {/* Modal */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{editingPessoa ? 'Editar Cadastro' : 'Novo Cadastro'}</h2>
              <button onClick={() => setDialogOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <select
                    data-testid="pessoa-tipo-select"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="input-field"
                  >
                    <option value="PF">Pessoa Física</option>
                    <option value="PJ">Pessoa Jurídica</option>
                  </select>
                </div>
                <div>
                  <Label>{formData.tipo === 'PF' ? 'Nome Completo' : 'Razão Social'}</Label>
                  <Input
                    data-testid="pessoa-nome-input"
                    value={formData.nome_razao}
                    onChange={(e) => setFormData({ ...formData, nome_razao: e.target.value })}
                    required
                  />
                </div>
              </div>

              {formData.tipo === 'PJ' && (
                <div>
                  <Label>Nome Fantasia</Label>
                  <Input
                    value={formData.fantasia}
                    onChange={(e) => setFormData({ ...formData, fantasia: e.target.value })}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{formData.tipo === 'PF' ? 'CPF' : 'CNPJ'}</Label>
                  <Input
                    data-testid="pessoa-cpfcnpj-input"
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: formatarCPFCNPJInput(e.target.value) })}
                    placeholder={formData.tipo === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                    maxLength={formData.tipo === 'PF' ? 14 : 18}
                    required
                  />
                </div>
                <div>
                  <Label>{formData.tipo === 'PF' ? 'RG' : 'IE'}</Label>
                  <Input
                    value={formData.rg_ie}
                    onChange={(e) => setFormData({ ...formData, rg_ie: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: formatarTelefone(e.target.value) })}
                    placeholder="(00) 0000-0000"
                    maxLength={15}
                  />
                </div>
                <div>
                  <Label>Celular</Label>
                  <Input
                    value={formData.celular}
                    onChange={(e) => setFormData({ ...formData, celular: formatarTelefone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    maxLength={16}
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Tipo de Cadastro</Label>
                <div className="flex items-center flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cliente"
                      data-testid="pessoa-cliente-checkbox"
                      checked={formData.cliente}
                      onCheckedChange={(checked) => setFormData({ ...formData, cliente: checked })}
                    />
                    <Label htmlFor="cliente" className="cursor-pointer">Cliente</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="fornecedor"
                      data-testid="pessoa-fornecedor-checkbox"
                      checked={formData.fornecedor}
                      onCheckedChange={(checked) => setFormData({ ...formData, fornecedor: checked })}
                    />
                    <Label htmlFor="fornecedor" className="cursor-pointer">Fornecedor</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="motorista"
                      data-testid="pessoa-motorista-checkbox"
                      checked={formData.motorista}
                      onCheckedChange={(checked) => setFormData({ ...formData, motorista: checked })}
                    />
                    <Label htmlFor="motorista" className="cursor-pointer">Motorista</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="funcionario"
                      data-testid="pessoa-funcionario-checkbox"
                      checked={formData.funcionario}
                      onCheckedChange={(checked) => setFormData({ ...formData, funcionario: checked })}
                    />
                    <Label htmlFor="funcionario" className="cursor-pointer">Funcionário</Label>
                  </div>
                </div>
                {formData.motorista && (
                  <div className="mt-3 flex items-end gap-3">
                    <div>
                      <Label htmlFor="percentual_acerto" className="text-sm text-slate-700">% Acerto do Motorista</Label>
                      <Input
                        id="percentual_acerto"
                        type="number" min="0" max="100" step="0.1"
                        placeholder="ex: 35"
                        value={formData.percentual_acerto}
                        onChange={(e) => setFormData({ ...formData, percentual_acerto: e.target.value })}
                        className="mt-1 w-36"
                      />
                    </div>
                    <p className="text-xs text-slate-400 pb-2">Preenchido automaticamente ao criar acerto</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button data-testid="save-pessoa-button" type="submit" disabled={saving} className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700">
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            data-testid="search-pessoas-input"
            placeholder="Buscar por nome, CPF/CNPJ ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nome/Razão Social</th>
                  <th>CPF/CNPJ</th>
                  <th>Email</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredPessoas.map((pessoa) => (
                  <tr key={pessoa.id} data-testid={`pessoa-row-${pessoa.id}`}>
                    <td>
                      {pessoa.tipo === 'PF' ? (
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-blue-600" />
                          <span>PF</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Building className="w-4 h-4 text-cyan-600" />
                          <span>PJ</span>
                        </div>
                      )}
                    </td>
                    <td className="font-semibold">{pessoa.nome_razao}</td>
                    <td>{formatCPFCNPJ(pessoa.cpf_cnpj)}</td>
                    <td>{pessoa.email || '-'}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {pessoa.cliente && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                            Cliente
                          </span>
                        )}
                        {pessoa.fornecedor && (
                          <span className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded">
                            Fornecedor
                          </span>
                        )}
                        {pessoa.motorista && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                            Motorista
                          </span>
                        )}
                        {pessoa.funcionario && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                            Funcionário
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${pessoa.ativo ? 'status-pago' : 'status-cancelado'}`}>
                        {pessoa.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          data-testid={`edit-pessoa-${pessoa.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(pessoa)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-pessoa-${pessoa.id}`}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setConfirmDelete({ isOpen: true, id: pessoa.id })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pessoas;